import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_family-connect-195/artifacts/d7hehvno_Color%20logo%20with%20background.svg';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="privacy-page">
      <header className="glass-header sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Kinsy" className="h-8" />
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm text-stone-600 hover:text-teal-600">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-4xl font-bold text-stone-900 tracking-tight mb-2">Privacy Policy</h1>
        <p className="text-sm text-stone-500 mb-10">Last updated: February 19, 2026</p>

        <div className="text-stone-800 space-y-6 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Who we are</h2>
            <p>
              Kinsy is operated by <strong>Kinsy LLC</strong> ("we", "us"). We build a private social network
              for families. Questions? Email <a className="text-teal-600 hover:underline" href="mailto:contact@kinsy.io">contact@kinsy.io</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">What we collect</h2>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Account info</strong> — your name, email, password (hashed with bcrypt) and avatar.</li>
              <li><strong>Content you share</strong> — posts, comments, reactions, photos, albums, events, RSVPs and family-tree details. This is visible only to your family circle.</li>
              <li><strong>Device tokens</strong> — push subscription endpoints stored if you opt-in to notifications.</li>
              <li><strong>Billing</strong> — Stripe handles all card data. We store your Stripe customer ID, plan and subscription status.</li>
              <li><strong>Diagnostics</strong> — basic logs (IP, user-agent, timestamps) used to keep the service healthy and detect abuse.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">How we use it</h2>
            <p>To run the app, deliver invites + reset emails (via SendGrid), notify you of family activity, fulfill photo-book orders (via Gelato), process payments (via Stripe), and prevent abuse. We never sell your data and we never use it to train AI on third parties.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Sharing</h2>
            <p>Only people invited to your family see your content. We share data with these processors only as needed to operate the service:</p>
            <ul className="list-disc list-inside space-y-1 mt-2">
              <li><strong>Stripe, Inc.</strong> — payments &amp; subscription management</li>
              <li><strong>SendGrid (Twilio)</strong> — transactional email</li>
              <li><strong>Gelato (Print-on-Demand)</strong> — physical photo book fulfillment, only when you order one</li>
              <li><strong>MongoDB Atlas + cloud hosting</strong> — encrypted storage of your data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Your rights</h2>
            <ul className="list-disc list-inside space-y-1">
              <li><strong>Access &amp; correction</strong> — see and edit your data in Profile.</li>
              <li><strong>Deletion</strong> — delete your account from Profile → Danger zone. We remove your personal content immediately. Family-only admins who delete will also remove the family if they are the only member left.</li>
              <li><strong>Export</strong> — email <a className="text-teal-600 hover:underline" href="mailto:contact@kinsy.io">contact@kinsy.io</a> for a JSON export of your content.</li>
              <li><strong>EU/UK/CA residents</strong> — GDPR/UK-GDPR/PIPEDA rights including data portability, restriction and complaint to your supervisory authority.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Retention</h2>
            <p>Account data lives until you delete it. Backups roll off after 30 days. Stripe transaction records are kept 7 years for tax compliance.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Children</h2>
            <p>Kinsy is intended for family members of all ages, but accounts must be created by someone aged 13 or older (16 in the EU/UK). Younger family members can be added as <em>placeholder</em> tree nodes without an account.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Security</h2>
            <p>We use TLS for all traffic, bcrypt for password hashing, signed JWT cookies for sessions, rate limiting on auth endpoints, and daily encrypted backups. No system is perfect — please report security issues to <a className="text-teal-600 hover:underline" href="mailto:contact@kinsy.io">contact@kinsy.io</a>.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Changes</h2>
            <p>If we materially change this policy we'll notify you in-app. Continued use means you accept the updated policy.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Contact</h2>
            <p>
              Kinsy LLC<br />
              <a className="text-teal-600 hover:underline" href="mailto:contact@kinsy.io">contact@kinsy.io</a>
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
